import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { X, BookOpen, Search, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ConceptCards - Primary learning mode for Basic Concepts
 * Displays concepts in a grid with expandable cards
 */
export default function ConceptCards({ 
  concepts = [], 
  userProgress = null,
  onProgressUpdate = null,
  onBack = null 
}) {
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [exploredConcepts, setExploredConcepts] = useState(
    new Set(userProgress?.concepts_explored || [])
  );

  // Filter concepts based on search
  const filteredConcepts = concepts.filter(concept => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      concept.title?.toLowerCase().includes(query) ||
      concept.definition?.toLowerCase().includes(query) ||
      concept.detailed_explanation?.toLowerCase().includes(query)
    );
  });

  // Handle concept exploration
  const handleConceptExplored = (conceptId) => {
    if (!conceptId) return;

    const newExplored = new Set(exploredConcepts);
    if (!newExplored.has(conceptId)) {
      newExplored.add(conceptId);
      setExploredConcepts(newExplored);

      // Update progress
      if (onProgressUpdate) {
        onProgressUpdate({
          concepts_explored: Array.from(newExplored),
          concept_cards_viewed: true
        });
      }
    }
  };

  // Open concept detail
  const handleConceptClick = (concept) => {
    setSelectedConcept(concept);
    handleConceptExplored(concept.id);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-white">📖 Concept Cards</h3>
        {onBack && (
          <Button
            onClick={onBack}
            variant="outline"
            size="sm"
            className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-white hover:bg-[hsl(var(--sidebar-hover))] hover:text-white hover:border-indigo-500"
          >
            ← Back
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search concepts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Concept Grid */}
      {filteredConcepts.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          {searchQuery ? 'No concepts found matching your search.' : 'No concepts available yet.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredConcepts.map((concept) => {
            const isExplored = exploredConcepts.has(concept.id);
            return (
              <div
                key={concept.id}
                onClick={() => handleConceptClick(concept)}
                className={cn(
                  "bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-4 cursor-pointer transition-all hover:border-indigo-500 hover:shadow-lg",
                  isExplored && "border-green-500/50"
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{concept.icon || '📚'}</span>
                    <h4 className="font-bold text-white">{concept.title}</h4>
                  </div>
                  {isExplored && (
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                  )}
                </div>
                <p className="text-gray-300 text-sm line-clamp-2">
                  {concept.definition || concept.detailed_explanation || 'Click to learn more...'}
                </p>
                {concept.real_world_examples && concept.real_world_examples.length > 0 && (
                  <div className="mt-2 text-xs text-indigo-400">
                    {concept.real_world_examples.length} real-world example{concept.real_world_examples.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Concept Detail Modal - Rendered via Portal to escape parent modal */}
      {selectedConcept && createPortal(
        <div 
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" 
          onClick={() => setSelectedConcept(null)}
          style={{ pointerEvents: 'auto' }}
        >
          <div 
            className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
            style={{ pointerEvents: 'auto' }}
          >
            {/* Header - Fixed at top */}
            <div className="flex items-start justify-between p-6 pb-4 flex-shrink-0 border-b border-[hsl(var(--card-border))]">
              <div className="flex items-center gap-3">
                <span className="text-4xl">{selectedConcept.icon || '📚'}</span>
                <div>
                  <h3 className="text-2xl font-bold text-white">{selectedConcept.title}</h3>
                  {exploredConcepts.has(selectedConcept.id) && (
                    <span className="text-xs text-green-400 flex items-center gap-1 mt-1">
                      <Check className="w-3 h-3" /> Explored
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('X button clicked, closing modal');
                  setSelectedConcept(null);
                }}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring h-8 w-8 px-0 text-gray-400 hover:text-white hover:bg-accent/50 flex-shrink-0 cursor-pointer"
                type="button"
                aria-label="Close concept detail"
                style={{ pointerEvents: 'auto', zIndex: 10 }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 p-6 min-h-0">

              {/* Definition */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-400 mb-2">What is it?</h4>
                <p className="text-white">
                  {selectedConcept.definition || selectedConcept.detailed_explanation || 'No definition available.'}
                </p>
              </div>

              {/* Detailed Explanation */}
              {selectedConcept.detailed_explanation && selectedConcept.detailed_explanation !== selectedConcept.definition && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-400 mb-2">Detailed Explanation</h4>
                  <p className="text-gray-300 whitespace-pre-line">
                    {selectedConcept.detailed_explanation}
                  </p>
                </div>
              )}

              {/* Real-World Examples */}
              {selectedConcept.real_world_examples && selectedConcept.real_world_examples.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-400 mb-2">🌍 Real-World Examples</h4>
                  <div className="space-y-3">
                    {selectedConcept.real_world_examples.map((example, idx) => (
                      <div key={idx} className="bg-[hsl(var(--main-bg))] rounded-lg p-3">
                        {example.title && (
                          <h5 className="font-semibold text-white mb-1">{example.title}</h5>
                        )}
                        {example.description && (
                          <p className="text-gray-300 text-sm">{example.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Concepts */}
              {selectedConcept.related_concepts && selectedConcept.related_concepts.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-400 mb-2">Related Concepts</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedConcept.related_concepts.map((relatedId, idx) => {
                      const relatedConcept = concepts.find(c => c.id === relatedId);
                      if (!relatedConcept) return null;
                      return (
                        <Button
                          key={idx}
                          onClick={() => {
                            setSelectedConcept(relatedConcept);
                            handleConceptExplored(relatedId);
                          }}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          {relatedConcept.title}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Close Button - Fixed at bottom */}
            <div className="border-t border-[hsl(var(--card-border))] p-4 bg-[hsl(var(--card-bg))] rounded-b-lg flex-shrink-0">
              <Button
                onClick={() => setSelectedConcept(null)}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                Close
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
